import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  TextField,
  Select,
  Button,
  useSnackbar,
  type SelectOption,
} from '@/ui'
import { LINE_KINDS, LINE_KIND_LABELS, catalogItemInputSchema, kindHasPartNumber, kindHasCost, type CatalogItem, type LineKind } from '@garage/shared'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { parseMoneyToMinor, formatMinor } from '@garage/shared'
import { ActivityInfo } from '@/components/ActivityInfo.js'

interface CatalogItemDialogProps {
  open: boolean
  item?: CatalogItem
  onClose: () => void
}

export function CatalogItemDialog({ open, item, onClose }: CatalogItemDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  // Form state
  const [kind, setKind] = useState<LineKind>('part')
  const [name, setName] = useState('')
  const [partNumber, setPartNumber] = useState('')
  const [costDisplay, setCostDisplay] = useState('')
  const [rateDisplay, setRateDisplay] = useState('')

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize form when dialog opens with an item
  useEffect(() => {
    if (open && item) {
      setKind(item.kind)
      setName(item.name)
      setPartNumber(item.partNumber || '')
      setCostDisplay(item.defaultCostMinor !== null ? formatMinor(item.defaultCostMinor, { decimals: 2 }) : '')
      setRateDisplay(formatMinor(item.defaultRateMinor, { decimals: 2 }))
      setErrors({})
    } else if (open && !item) {
      // Reset for new item
      setKind('part')
      setName('')
      setPartNumber('')
      setCostDisplay('')
      setRateDisplay('')
      setErrors({})
    }
  }, [open, item])

  // Mutation for create/update
  const { mutate, isPending } = useMutation({
    mutationFn: async (input: Parameters<typeof api.catalog.create>[0]) => {
      if (item) {
        return api.catalog.update(item.id, input)
      }
      return api.catalog.create(input)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.catalog() })
      snackbar.success(item ? t('storage.itemUpdated') : t('catalogItemDialog.itemAdded'))
      onClose()
    },
    onError: (err: Error) => {
      if (err instanceof ApiError) {
        // Handle 409 duplicate as a name field error
        if (err.status === 409) {
          setErrors({ name: err.message })
        } else {
          setErrors(err.fieldErrors)
        }
      } else {
        snackbar.error(t('catalogItemDialog.failedToSaveItem'))
      }
    },
  })

  const handleSubmit = () => {
    setErrors({})

    // Parse money fields
    const costMinor = costDisplay.trim() ? parseMoneyToMinor(costDisplay) : null
    const rateMinor = parseMoneyToMinor(rateDisplay)

    const input = {
      kind,
      name,
      partNumber: partNumber.trim() || null,
      defaultCostMinor: costMinor,
      defaultRateMinor: rateMinor,
    }

    // Validate
    const result = catalogItemInputSchema.safeParse(input)
    if (!result.success) {
      const fieldErrs: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const key = issue.path.join('.')
        if (key && !(key in fieldErrs)) {
          fieldErrs[key] = issue.message
        }
      }
      setErrors(fieldErrs)
      return
    }

    mutate(result.data)
  }

  const kindOptions: SelectOption[] = LINE_KINDS.map(k => ({
    value: k,
    label: t(`catalog.kinds.${k}`, LINE_KIND_LABELS[k]),
  }))

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={item ? t('catalogItemDialog.editCatalogItem') : t('catalogItemDialog.addCatalogItem')}
      size="md"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" loading={isPending} onClick={handleSubmit}>
            {item ? t('catalogItemDialog.updateAction') : t('common.add')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {item && <ActivityInfo entityType="catalog_item" entityId={item.id} />}
        <Select
          label={t('catalog.columns.kind')}
          value={kind}
          onChange={(v: string) => setKind(v as LineKind)}
          options={kindOptions}
          disabled={!!item}
          error={errors.kind}
          fullWidth
        />

        <TextField
          label={t('inspectionTemplate.name')}
          value={name}
          onChange={setName}
          error={errors.name}
          fullWidth
        />

        {kindHasPartNumber(kind) && (
          <TextField
            label={t('catalogItemDialog.partNumber')}
            value={partNumber}
            onChange={setPartNumber}
            error={errors.partNumber}
            fullWidth
          />
        )}

        {kindHasCost(kind) && (
          <TextField
            label={t('catalog.columns.ourCost')}
            value={costDisplay}
            onChange={setCostDisplay}
            numeric
            supportingText={t('catalogItemDialog.internalCostHelp')}
            error={errors.defaultCostMinor}
            fullWidth
          />
        )}

        <TextField
          label={t('catalog.columns.clientRate')}
          value={rateDisplay}
          onChange={setRateDisplay}
          numeric
          error={errors.defaultRateMinor}
          fullWidth
        />
      </div>
    </Dialog>
  )
}
